/**
 * Build a vent hood above a range placement.
 *
 * Width matches the range. Mounted `VENT_HOOD_GAP_ABOVE_STOVE` above
 * the cooktop top.
 *   - `buildVentHood`: wall-mounted, back face on the wall surface. Its TOP
 *     is the uppers' top line (`opts.topY`): the body slopes back to a
 *     cabinet-depth top that sits flush with the wall cabinets either side
 *     (`VENT_HOOD_TOP_DEPTH`, "top flush with upper cabinets"). The standard
 *     24" height was that line written as a constant — 60" + 24" is the 84" a
 *     30" upper tops out at — so uppers hung at 22", or to the ceiling, left
 *     the hood 4" to a foot short of the cabinets it stands between.
 *   - `buildFloatingVentHood`: free-standing, centered above the range
 *     with a square chimney that rises to the ceiling. Used for ranges
 *     on peninsula walls where there's no real wall behind the hood.
 */

const {
  VENT_HOOD_DEPTH,
  VENT_HOOD_HEIGHT,
  VENT_HOOD_GAP_ABOVE_STOVE,
} = require('./constants');
const { getWallAxes } = require('./wallMath');

/** Below this a hood is a sliver, not a hood — the upper L's own floor. */
const MIN_HOOD_HEIGHT = 0.05;
/** Float dust between two derivations of the same 84" line. */
const SAME_LINE = 1e-6;

/**
 * The wall hood's height: from its cooktop clearance up to the uppers' top
 * line. The STANDARD height is returned as the constant whenever the line is
 * the standard line, so a kitchen nobody re-hung gets the same number it
 * always had rather than one that differs in the sixteenth digit.
 *
 * @returns {number|null} null when the line leaves no room for a hood
 */
function hoodHeightTo(topY, bottomY) {
  if (typeof topY !== 'number') return VENT_HOOD_HEIGHT;
  const rise = topY - bottomY;
  if (Math.abs(rise - VENT_HOOD_HEIGHT) < SAME_LINE) return VENT_HOOD_HEIGHT;
  return rise > MIN_HOOD_HEIGHT ? rise : null;
}

/**
 * @param {Object} placement - Candidate placement record (range).
 *   Must include `wallParam`, `width`, and (for range) `height`.
 * @param {Object} wall
 * @param {number} floorY
 * @param {Object} [opts]
 * @param {number} [opts.topY] World Y of the uppers' top line. Absent — a run
 *   with its uppers off, a caller with no band — the hood is the standard
 *   height: there is no line for it to meet.
 * @param {number} [opts.chimneyTopY] World Y a CHIMNEY cover rises to above
 *   the hood — a stacked row's top (`upperBand.hoodChimneyTopY`). It is
 *   render-only (`dimensions.chimneyHeight`, the wall chimney hood), never
 *   the hood's box: the hood's own height is unchanged. Absent — no chimney.
 * @returns {Object|null} Vent hood object, or null on degenerate input (and
 *   when the top line leaves no room for a hood above the cooktop clearance).
 */
function buildVentHood(placement, wall, floorY, opts = {}) {
  const axes = getWallAxes(wall);
  if (!axes) return null;
  const ry = axes.wallRotationY;

  const cooktopTopY = floorY + (placement.height || 0);
  const bottomY = cooktopTopY + VENT_HOOD_GAP_ABOVE_STOVE;
  const hoodHeight = hoodHeightTo(opts.topY, bottomY);
  if (hoodHeight == null) return null;
  const centerY = bottomY + hoodHeight / 2;

  const center = {
    x:
      axes.start.x +
      placement.wallParam * axes.wallDir.x +
      (VENT_HOOD_DEPTH / 2) * axes.wallNormal.x,
    y: centerY,
    z:
      axes.start.z +
      placement.wallParam * axes.wallDir.z +
      (VENT_HOOD_DEPTH / 2) * axes.wallNormal.z,
  };

  const stamp = `${placement.applianceId}`;
  const chimneyHeight =
    typeof opts.chimneyTopY === 'number'
      ? opts.chimneyTopY - (bottomY + hoodHeight)
      : 0;

  return {
    id: `vent_hood_${stamp}`,
    type: 'vent-hood',
    subtype: 'chimney',
    label: 'Range Hood',
    category: { 'vent-hood': true },
    center,
    dimensions: {
      x: placement.width,
      y: hoodHeight,
      z: VENT_HOOD_DEPTH,
      // the duct cover over the hood, up to a stacked row's top
      ...(chimneyHeight > MIN_HOOD_HEIGHT ? { chimneyHeight } : {}),
    },
    rotation: { x: 0, y: ry, z: 0 },
    parentWallIds: [wall.id],
    // The hood is part of the RANGE's installation: sharing its group is
    // what makes the run's appliance move carry the hood along (a clamp that
    // slid the range 12" left its hood behind — user-seen 2026-08-31).
    fixtureGroupId: placement.fixtureGroupId || `kitchenVentHood_${stamp}`,
    customFrontClearance: 0,
    addedBy: 'kitchen-layout',
    lastModified: new Date().toISOString(),
  };
}

/**
 * Build a free-standing (island / peninsula) vent hood above a range
 * placement. The hood body is centered above the cooktop (no wall to
 * push against); a chimney rises from the hood's top to the ceiling.
 * The bounding box stays at the hood body's dimensions — the chimney
 * is a render-only addition, not part of the collision footprint —
 * driven by `dimensions.chimneyHeight` on the returned object.
 *
 * @param {Object} placement - Candidate placement record (range).
 * @param {Object} wall - The (virtual) wall the range is placed on,
 *   used to derive rotation and to position the hood centered over
 *   the range footprint.
 * @param {number} floorY
 * @param {number} ceilingY - World-Y of the ceiling so we know how
 *   tall to make the chimney.
 * @returns {Object|null} Vent hood object, or null on degenerate input.
 */
function buildFloatingVentHood(placement, wall, floorY, ceilingY, ceilingId) {
  const axes = getWallAxes(wall);
  if (!axes) return null;
  const ry = axes.wallRotationY;

  const cooktopTopY = floorY + (placement.height || 0);
  const bottomY = cooktopTopY + VENT_HOOD_GAP_ABOVE_STOVE;
  const hoodTopY = bottomY + VENT_HOOD_HEIGHT;
  const centerY = bottomY + VENT_HOOD_HEIGHT / 2;
  const chimneyHeight = Math.max(0, ceilingY - hoodTopY);

  // Centered above the range. The range sits `placement.depth / 2`
  // forward of the (virtual) wall surface along the inward normal —
  // mirror that offset so the hood's footprint aligns with the
  // range's, not with the wall.
  const stoveCenterOffset = (placement.depth || 0) / 2;
  const center = {
    x:
      axes.start.x +
      placement.wallParam * axes.wallDir.x +
      stoveCenterOffset * axes.wallNormal.x,
    y: centerY,
    z:
      axes.start.z +
      placement.wallParam * axes.wallDir.z +
      stoveCenterOffset * axes.wallNormal.z,
  };

  const stamp = `${placement.applianceId}`;

  return {
    id: `vent_hood_${stamp}`,
    type: 'vent-hood',
    subtype: 'chimney',
    label: 'Island Range Hood',
    category: { 'vent-hood': true, island: true },
    center,
    dimensions: {
      x: placement.width,
      y: VENT_HOOD_HEIGHT,
      z: VENT_HOOD_DEPTH,
      // Render-only metadata for the procedural renderer.
      floating: true,
      chimneyHeight,
    },
    rotation: { x: 0, y: ry, z: 0 },
    parentWallIds: [wall.id],
    // Parent the hood to the kitchen ceiling so it groups with other
    // ceiling-attached fixtures (lights, etc.) in selection / dollhouse
    // hide logic.
    ...(ceilingId ? { parentCeilingId: ceilingId } : {}),
    // The hood is part of the RANGE's installation: sharing its group is
    // what makes the run's appliance move carry the hood along (a clamp that
    // slid the range 12" left its hood behind — user-seen 2026-08-31).
    fixtureGroupId: placement.fixtureGroupId || `kitchenVentHood_${stamp}`,
    customFrontClearance: 0,
    addedBy: 'kitchen-layout',
    lastModified: new Date().toISOString(),
  };
}

module.exports = { buildVentHood, buildFloatingVentHood };
